/* global Vue, VueI18n, ALGOLIA_APP_ID, ALGOLIA_PUBLIC_API_KEY, ALGOLIA_INDEX, SEARCH_SUGGESTIONS, algoliasearch, LOCALE, PROPERTIES_URL, OFFERS_URL, Swiper */

import { messagesSpecific } from '../i18n/translations'
import { CreateI18n } from '../i18n/create-i18n'
import FocusedCard from './components/FocusedCard'

const i18n = CreateI18n(messagesSpecific)

let hero = null

const FocusedSearchApp = Vue.createApp({
  name: 'FocusedSearchApp',
  components: { FocusedCard },
  template: `
  <div class="focused-search" @scroll="onScroll" @touchstart="onTouchStart" :class="{'scrolled-down': isScrolledDown}">
    <div class="focused-search__banner">
      <video ref="videoRef" muted loop :class="{'is-playing': playVideo}" :src="hero.video.replace('/upload/', '/upload/c_fill,g_auto,w_1920,h_1080/q_auto,f_auto/')" type="video/webm"></video>
      <picture>
        <source
          media="(min-width: 1440px)"
          :srcset="hero.image.replace('/upload/', '/upload/w_1440,q_auto,f_auto/')">
        <source
          media="(min-width: 992px)"
          :srcset="hero.image.replace('/upload/', '/upload/w_992,q_auto,f_auto/')">
        <source
          media="(min-width: 1440px)"
          srcset="hero.image.replace('/upload/', '/upload/w_1440,h_900,q_auto,f_auto/')">
        <img
          :src="hero.image.replace('/upload/', '/upload/w_576,h_900,q_auto,f_auto/')"
          alt=""
          class="focused-search__poster"
          width="1440"
          height="900">
      </picture>
      <div class="focused-search__hero">
        <div class="container grid">
          <div class="focused-search__content col-12 col-sm-8 offset-sm-2 col-lg-6 offset-lg-3">
            <div v-if="!isActive">
              <p class="focused-search__supertitle">{{ hero.supertitle }}</p>

              <h1 class="hero-title">{{ hero.title }}</h1>

              <div class="focused-search__description h4">
                {{ hero.description }}
              </div>
            </div>

            <div class="focused-search__input">
              <label>
                <i class="icon icon-focus-search"></i>
                <input ref="searchFieldRef" autocomplete="off" v-model="searchQuery" type="text" class="input" :placeholder="t('focusedSearch.placeholder')" :aria-label="t('focusedSearch.placeholder')" @focus="openSearch" />
              </label>
              <button v-if="isActive"><i class="icon icon-close" @click="closeSearch"></i></button>
            </div>
            <div class="focused-search__suggestions" v-if="isActive && searchQuery === '' && (!isMobile || !isScrolledDown)">
              <button v-for="suggestion in suggestions" @click="searchQuery = suggestion.query">{{ suggestion.title }}</button>
            </div>
          </div>
        </div>
      </div>
    </div>
    <div class="focused-search__results" v-if="isActive">
      <div class="container grid">
        <div v-if="searchQuery === ''" class="col-12 col-lg-4" v-for="category in resultCategories">
          <h3>{{ category.title }}</h3>
          <div class="focused-search__category" :class="{'focused-search__category--destinations': category.type === 'destination' }">
            <FocusedCard v-for="result in category.results" :result="result" />
          </div>
        </div>
        <div v-else-if="resultCategories.length === 0" class="col-12">
          <h3>{{ t('searchApp.noResults') }}</h3>
          <div>{{ t('focusedSearch.searchFor') }}</div>
          <div class="focused-search__suggestions">
            <button v-for="suggestion in suggestions" @click="searchQuery = suggestion.query">{{ suggestion.title }}</button>
          </div>
        </div>
        <div v-else-if="isMobile" class="col-12">
          <div class="focused-search__category">
            <FocusedCard v-for="result in resultsFlat" :result="result" />
          </div>
        </div>
        <div v-else class="col-12" v-for="category in resultCategories">
          <div class="category-title">
            <h3>{{ category.title }} <a :href="category.url">{{ t('focusedSearch.showAll') }} <i class="icon-arrow-right-long"></i></a></h3>
          </div>
          <div class="swiper-container">
            <div class="swiper-button-prev"><i class="icon-caret-left"></i></div>
            <div class="focused-search__category" class="swiper" ref="swiperRef" >
              <div class="swiper-wrapper">
                <div class="swiper-slide" v-for="result in category.results">
                  <FocusedCard :result="result" />
                </div>
              </div>
            </div>
            <div class="swiper-button-next"><i class="icon-caret-right"></i></div>
          </div>
        </div>
      </div>
    </div>
  </div>
  `,
  setup () {
    const { t } = VueI18n.useI18n()
    const videoRef = Vue.ref(null)
    const swiperRef = Vue.ref(null)
    const searchFieldRef = Vue.ref(null)
    const isActive = Vue.ref(false)
    const isScrolledDown = Vue.ref(false)
    const playVideo = Vue.ref(false)
    const suggestions = SEARCH_SUGGESTIONS.filter(s => s.location.includes('home'))
    const searchQuery = Vue.ref('')
    const resultsByCategory = Vue.ref([])
    const resultsFlat = Vue.ref([])
    const resultCategories = Vue.computed(() => {
      if (searchQuery.value === '') {
        return [
          { title: t('focusedSearch.properties'), type: 'property', results: resultsByCategory.value?.[0] || [] },
          { title: t('focusedSearch.specialOffers'), type: 'offer', results: resultsByCategory.value?.[1] || [] },
          { title: t('destinations'), type: 'destination', results: resultsByCategory.value?.[2] || [] },
        ]
      } else {
        const categories = [
          { title: t('checkRates.hotelsResorts'), type: 'hotels', url: PROPERTIES_URL, results: [] },
          { title: t('checkRates.campsites'), types: 'campsites', url: `${PROPERTIES_URL}?type=camping`, results: [] },
          { title: t('focusedSearch.specialOffers'), types: 'special-offers', url: OFFERS_URL, results: [] },
        ]
        return resultsFlat.value.reduce((acc, hit) => {
          if (hit.type === 'offer') {
            acc[2].results.push(hit)
            return acc
          }

          if (hit.brand[0] === 'camping') {
            acc[1].results.push(hit)
            return acc
          }

          acc[0].results.push(hit)
          return acc
        }
        , categories).filter(category => category.results.length > 0)
      }
    })

    const searchClient = algoliasearch(
      ALGOLIA_APP_ID,
      ALGOLIA_PUBLIC_API_KEY,
    )
    const searchIndex = searchClient.initIndex(ALGOLIA_INDEX)

    const isMobile = Vue.ref(true)
    function handleResize () {
      isMobile.value = !window.matchMedia('(min-width: 1200px)').matches
    }

    Vue.onMounted(() => {
      searchFieldRef.value.value = ''
      videoRef.value.play()
      playVideo.value = true

      searchFieldRef.value.addEventListener('keyup', (e) => {
        if (e.key === 'Enter' || e.keyCode === 13) {
          searchFieldRef.value.blur()
        }
      })

      window.addEventListener('resize', handleResize)
      handleResize()
    })

    Vue.onBeforeUnmount(() => {
      window.removeEventListener('resize', handleResize)
    })

    function resetSearchResults () {
      const filters = [
        [`lang:${LOCALE === 'si' ? 'sl' : LOCALE}`],
        ['searchable:true'],
      ]

      searchClient
        .search([
          { indexName: ALGOLIA_INDEX, params: { hitsPerPage: 3, facetFilters: [...filters, ['type:property']] } },
          { indexName: ALGOLIA_INDEX, params: { hitsPerPage: 3, facetFilters: [...filters, ['type:offer']] } },
          { indexName: ALGOLIA_INDEX, params: { hitsPerPage: 4, facetFilters: [...filters, ['type:destination']] } },
        ])
        .then(({ results }) => {
          resultsByCategory.value = results.map(result => result.hits)
        })
    }

    function resetSwiper () {
      Vue.nextTick(() => {
        if (swiperRef.value) {
          swiperRef.value.forEach((swiperEl, index) => {
            if (swiperEl.swiper) {
              swiperEl.swiper.destroy()
            }
            // eslint-disable-next-line no-unused-vars
            const swiper = new Swiper(swiperEl, {
              loop: false,
              slidesPerView: 3,
              spaceBetween: 45,
              navigation: {
                nextEl: swiperEl.nextElementSibling,
                prevEl: swiperEl.previousElementSibling,
              },
            })
          })
        }
      })
    }

    Vue.watch(searchQuery, (query) => {
      if (query.length === 0) {
        resetSearchResults()
        return
      }

      const filters = [
        [`lang:${LOCALE === 'si' ? 'sl' : LOCALE}`],
        ['searchable:true'],
        ['type:property', 'type:offer'],
      ]

      searchIndex
        .search(query, { facetFilters: filters, hitsPerPage: 100 })
        .then(({ hits }) => {
          resultsFlat.value = hits
          resultsByCategory.value = []

          resetSwiper()
        })
    })

    Vue.watch(isMobile, () => {
      resetSwiper()
    })

    function openSearch () {
      if (isActive.value) {
        return
      }

      isActive.value = true
      videoRef.value.pause()
      window.scrollTo({ top: 0 })

      resetSearchResults()

      // Make sure that when virtual keyboard opens, it takes the new position as a reference point
      if (isMobile.value) {
        searchFieldRef.value.blur()
        Vue.nextTick(() => {
          searchFieldRef.value.focus()
        })
      }
    }

    function closeSearch () {
      searchQuery.value = ''
      isActive.value = false
      if (!isMobile.value) {
        videoRef.value.play()
      }
    }

    function onScroll (s) {
      isScrolledDown.value = s.target.scrollTop > 0
      searchFieldRef.value.blur()
    }

    function onTouchStart () {
      if (isMobile.value && searchFieldRef.value) {
        searchFieldRef.value.blur()
      }
    }

    Vue.watch(isActive, (show) => {
      if (show) {
        document.body.classList.add('focused-search-active')
      } else {
        document.body.classList.remove('focused-search-active')
      }
    })

    return {
      openSearch,
      closeSearch,
      onScroll,
      onTouchStart,
      isScrolledDown,
      isActive,
      isMobile,
      searchQuery,
      t,
      videoRef,
      swiperRef,
      searchFieldRef,
      hero,
      playVideo,
      suggestions,
      resultCategories,
      resultsByCategory,
      resultsFlat,
    }
  },
})

const appElement = document.getElementById('focused-search-app')

if (appElement) {
  hero = JSON.parse(appElement.dataset.hero)
  FocusedSearchApp.use(i18n)
  FocusedSearchApp.mount(appElement)
}

export default FocusedSearchApp
